import React from 'react';

import { datadogRum } from '@datadog/browser-rum';
import cx from 'classnames';
import moment from 'moment-timezone';

import AddToCalendar from 'components/AddToCalendar';
import { FormsWaiversCta } from 'components/FormsWaiversCta/FormsWaiversCta';
import { PostBookingSurvey } from 'components/PostBookingSurvey/PostBookingSurvey';

import { useAppointmentForm } from 'hooks/appointment/useAppointmentForm';
import { useThirdPartyFormUrl } from 'hooks/form/useThirdPartyFormUrl';

import { TAppointment, TAppointmentContact } from 'types/appointment';
import { Form, FormStatus } from 'types/forms-and-waivers';

import s from './Success.module.css';

function formatDate(date: moment.MomentInput) {
  return moment(date).format('MMMM D, YYYY - h:mm a');
}

type FormDataP = {
  [x: string]: {
    trackingFormUrl?: string | null;
  } & Form;
};

type SuccessProps = {
  appointments: {
    address: string;
    appointmentGuid: string;
    appointmentTime: string;
    professionalName?: string;
    servicesNames: string[];
    status: TAppointment['status'];
  }[];
  clientInfo: Pick<TAppointmentContact, 'firstName' | 'email' | 'phone'>;
};

// This component is only for getting and returning form data. It doesn't render anything.
const GetFormData = ({
  appointmentGuid,
  callback,
}: {
  appointmentGuid: string;
  callback: (data: FormDataP) => void;
}) => {
  const { data: formData } = useAppointmentForm(appointmentGuid);
  const trackingFormUrl = useThirdPartyFormUrl(appointmentGuid, 'booking');

  React.useEffect(() => {
    if (formData && trackingFormUrl !== undefined) {
      callback({ [appointmentGuid]: { ...formData, trackingFormUrl } });
    }
  }, [appointmentGuid, formData, trackingFormUrl, callback]);

  return null;
};

export const Success = ({ appointments, clientInfo }: SuccessProps) => {
  const [formData, setFormData] = React.useState<FormDataP>({});

  const handleSetFormData = React.useCallback((data: FormDataP) => {
    setFormData((previous) => ({ ...previous, ...data }));
  }, []);

  React.useEffect(() => {
    datadogRum.addAction('booking_completed');
  }, []);

  const appointmentInfo = appointments.map(({ appointmentGuid }) => {
    const form = formData[appointmentGuid];

    return {
      appointmentGuid: appointmentGuid,
      formStatus: form?.status,
      questions: form?.questions,
      thirdPartyFormUrl: form?.trackingFormUrl,
      waivers: form?.waivers,
    };
  });

  const hasForms = appointmentInfo.some((appt) => {
    const {
      appointmentGuid,
      formStatus,
      questions,
      waivers,
      thirdPartyFormUrl,
    } = appt;

    return (
      (appointmentGuid &&
        formStatus &&
        formStatus !== FormStatus.not_required &&
        (questions?.length || waivers?.length)) ||
      thirdPartyFormUrl
    );
  });

  const hasPendingAppointments = appointments.some(
    (appointment) =>
      appointment.status === 'pending' || appointment.status === 'unapproved',
  );

  return (
    <div className={s.root}>
      <h1 className={s.title}>
        {hasPendingAppointments
          ? 'Appointment Request Received'
          : 'Looking forward to our appointment'}
      </h1>

      {hasPendingAppointments && (
        <p>
          We&apos;ve received your request and will update you via email/text
          message once it has been reviewed.
        </p>
      )}

      <FormsWaiversCta
        appointmentInfo={appointmentInfo}
        newTab
        subtitle={
          hasPendingAppointments
            ? 'Please take a moment to complete the necessary forms below while we review your appointment request.'
            : 'Please take the time to complete prior to your appointment.'
        }
      />

      <section className={s.detailsSection}>
        <h2 className={s.subtitle}>
          {hasPendingAppointments
            ? 'Requested Appointment Details'
            : 'Appointment Details'}
        </h2>

        {appointments.map((appointment) => {
          const {
            address,
            appointmentGuid,
            appointmentTime,
            professionalName,
            servicesNames,
          } = appointment;

          return (
            <div key={appointmentGuid}>
              <GetFormData
                key={appointmentGuid}
                appointmentGuid={appointmentGuid}
                callback={handleSetFormData}
              />

              <div className={cx(s.detailsWrap, s.appointmentDetails)}>
                <div className={s.detailsItem}>
                  {formatDate(appointmentTime)}
                </div>
                <div className={s.detailsItem}>
                  {servicesNames.join(', ')}{' '}
                  {professionalName && `with ${professionalName}`}
                </div>
                {address?.length > 0 && (
                  <div className={s.detailsItem}>{address}</div>
                )}
              </div>
              <div className={s.detailsWrap}>
                <AddToCalendar
                  appointmentGuid={appointmentGuid}
                  source="booking"
                />
              </div>
            </div>
          );
        })}
      </section>
      <PostBookingSurvey hasForms={hasForms} clientInfo={clientInfo} />
    </div>
  );
};
